import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  inject,
  input,
  viewChild,
  output,
  signal,
  computed,
} from "@angular/core";
import {
  AppConfigService,
  WindowEventService,
  CustomerParam,
  DeviceService,
  ConsentRequestAction,
  ConsentResponseAction,
  IDisclaimerCheckbox,
  IInteraction,
  localizeDisclaimer,
} from "../../../core-ui";
import { LocaleService } from "../../services/locale.service";

/** px tolerance, so that sub-pixel rounding does not keep the button disabled */
const SCROLL_END_TOLERANCE = 4;

@Component({
  selector: "app-disclaimer",
  templateUrl: "./disclaimer.component.html",
  styleUrls: ["./disclaimer.component.scss"],
  standalone: false,
})
export class DisclaimerComponent implements OnInit, AfterViewInit, OnDestroy {
  private config = inject(AppConfigService);
  private windowService = inject(WindowEventService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private locale = inject(LocaleService);

  readonly interaction = input<IInteraction>(undefined);

  text: string;

  /** emits the html snapshot of what the customer consented to */
  readonly accepted = output<string>();
  readonly rejected = output<void>();

  readonly acceptRef = viewChild("acceptRef", { read: ElementRef });
  readonly bodyRef = viewChild("bodyRef", { read: ElementRef });

  readonly scrollRequired = signal(false);
  readonly scrolledToEnd = signal(false);
  readonly checkboxes = signal<IDisclaimerCheckbox[]>([]);

  private readonly checkedIds = signal<ReadonlySet<string>>(new Set());

  readonly requiredChecked = computed(() =>
    this.checkboxes()
      .filter((checkbox) => checkbox.required)
      .every((checkbox) => this.checkedIds().has(checkbox.id))
  );
  readonly acceptDisabled = computed(
    () =>
      (this.scrollRequired() && !this.scrolledToEnd()) || !this.requiredChecked()
  );

  private resizeObserver: ResizeObserver;

  ngOnInit(): void {
    const language = this.locale.getLanguage();
    this.text = localizeDisclaimer(
      this.config.customerParam(CustomerParam.DISCLAIMER_TEXT),
      this.config.customerParamValue(
        CustomerParam.DISCLAIMER_TEXT_TRANSLATIONS
      ),
      language
    );
    this.scrollRequired.set(
      this.config.customerParamEnabled(CustomerParam.DISCLAIMER_SCROLL_REQUIRED)
    );
    // resolve the labels once, so the snapshot records what the customer saw
    const checkboxes: IDisclaimerCheckbox[] =
      this.config.customerParamValue(CustomerParam.DISCLAIMER_CHECKBOXES) ?? [];
    this.checkboxes.set(
      checkboxes.map((checkbox) => ({
        ...checkbox,
        label: localizeDisclaimer(
          checkbox.label,
          checkbox.labelTranslations,
          language
        ),
      }))
    );
    this.windowService.init(this.interaction().getParentFrameUrl());
    if (DeviceService.isIFrame) {
      this.windowService.sendMessage(new ConsentRequestAction());
    }
  }

  ngAfterViewInit(): void {
    if (this.scrollRequired()) {
      this.checkScrollPosition();
      // the text is rendered through innerHTML, so its height may still change
      // (fonts, images). re-evaluate whenever the scroll container resizes.
      const element = this.bodyRef()?.nativeElement;
      if (element && typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() =>
          this.checkScrollPosition()
        );
        this.resizeObserver.observe(element);
      }
    }
    // the copy is authored by the admin and may contain links. we are often
    // rendered inside the customer's page, so never navigate away from it.
    this.host.nativeElement
      .querySelectorAll("a[href]")
      .forEach((anchor: HTMLAnchorElement) => {
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
      });

    this.focusFirstAction();
  }

  /** start the customer off on whatever is keeping them from continuing */
  private focusFirstAction() {
    if (!this.acceptDisabled()) {
      this.acceptRef().nativeElement.focus();
      return;
    }
    if (this.scrollRequired() && !this.scrolledToEnd()) {
      this.bodyRef()?.nativeElement.focus();
      return;
    }
    this.host.nativeElement
      .querySelector<HTMLInputElement>(".consent-item input")
      ?.focus();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /** actions */

  checkScrollPosition() {
    const element: HTMLElement = this.bodyRef()?.nativeElement;
    if (!element) {
      return;
    }
    const reachedEnd =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - SCROLL_END_TOLERANCE;
    if (reachedEnd) {
      this.scrolledToEnd.set(true);
    }
  }

  isChecked(checkbox: IDisclaimerCheckbox) {
    return this.checkedIds().has(checkbox.id);
  }

  checkboxChanged(checkbox: IDisclaimerCheckbox, checked: boolean) {
    this.checkedIds.update((ids) => {
      const next = new Set(ids);
      if (checked) {
        next.add(checkbox.id);
      } else {
        next.delete(checkbox.id);
      }
      return next;
    });
  }

  exit() {
    if (DeviceService.isIFrame) {
      this.windowService.sendMessage(new ConsentResponseAction(false));
    }
    this.rejected.emit();
  }

  accept() {
    if (this.acceptDisabled()) {
      return;
    }
    if (DeviceService.isIFrame) {
      this.windowService.sendMessage(new ConsentResponseAction(true));
    }
    this.accepted.emit(this.buildSnapshot());
  }

  /** the consent copy as the customer saw it, plus what they ticked */
  private buildSnapshot(): string {
    const checkboxes = this.checkboxes();
    if (!checkboxes.length) {
      return this.text;
    }
    const items = checkboxes
      .map(
        (checkbox) =>
          `<li>[${this.isChecked(checkbox) ? "x" : " "}] ${checkbox.label}</li>`
      )
      .join("");
    return `${this.text ?? ""}<ul>${items}</ul>`;
  }
}
